import { env } from '../config/env';

export type ClientLogLevel = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

const LEVEL_RANK: Record<ClientLogLevel, number> = {
  fatal: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
  trace: 5,
};

const VALID_LEVELS: ClientLogLevel[] = ['fatal', 'error', 'warn', 'info', 'debug', 'trace'];

export const LOG_LEVEL_HEADER = 'X-Log-Level';
export const LOG_LEVEL_COOKIE = 'log_level';
export const LOG_LEVEL_QUERY = 'logLevel';

export function parseClientLogLevel(raw?: string | null): ClientLogLevel | undefined {
  if (!raw) return undefined;
  const normalized = raw.trim().toLowerCase();
  return VALID_LEVELS.includes(normalized as ClientLogLevel)
    ? (normalized as ClientLogLevel)
    : undefined;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : undefined;
}

function defaultLevelForMode(): ClientLogLevel {
  if (env.isTest) return 'warn';
  if (env.isDevelopment) return 'debug';
  return 'info';
}

/** Client precedence: query/cookie override > VITE_LOG_LEVEL > per-mode default. */
export function resolveClientLogLevel(): ClientLogLevel {
  if (typeof window !== 'undefined') {
    const fromQuery = parseClientLogLevel(
      new URLSearchParams(window.location.search).get(LOG_LEVEL_QUERY)
    );
    if (fromQuery && (env.enableDebugMode || isAdminSession())) return fromQuery;

    const fromCookie = parseClientLogLevel(readCookie(LOG_LEVEL_COOKIE));
    if (fromCookie && (env.enableDebugMode || isAdminSession())) return fromCookie;
  }

  return parseClientLogLevel(env.logLevel) ?? defaultLevelForMode();
}

function isAdminSession(): boolean {
  try {
    const raw = sessionStorage.getItem('creatoros_user_roles');
    if (!raw) return false;
    const roles: string[] = JSON.parse(raw);
    return roles.includes('admin') || roles.includes('super-admin');
  } catch {
    return false;
  }
}

export function shouldClientLog(level: ClientLogLevel): boolean {
  const threshold = resolveClientLogLevel();
  return LEVEL_RANK[level] <= LEVEL_RANK[threshold];
}

export function getClientLogLevelHeader(): string | undefined {
  const level = resolveClientLogLevel();
  const defaultLevel = defaultLevelForMode();
  if (level === defaultLevel) return undefined;
  if (!env.enableDebugMode && !isAdminSession()) return undefined;
  return level;
}
